/**
 * AppMaintenanceBanner Component
 *
 * Displays a maintenance/outage banner when the application is unavailable.
 *
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Content to render when app is available
 * @returns {React.ReactNode} Either the children content or the outage banner
 *
 * @description
 * This component polls the app availability endpoint to determine if the application
 * is currently experiencing an outage. If an outage is detected, it displays a banner
 * with a warning icon and informative messages. Otherwise, it renders the child content.
 *
 * The component uses the useAppAvailabilityPoll hook to check the availability status
 * at the configured API endpoint.
 */

import AppLogo from './common/AppLogo'
import useAppAvailabilityPoll from './common/hooks/useAppAvailabilityPoll'
import styles from './scss/AppMaintenanceBanner.module.scss'

export default function AppMaintenanceBanner({ children, lockAwareFetch }) {
  const isOutageHappened = useAppAvailabilityPoll(
    `${window?.env?.VITE_IAR_API}/api/app-availability`,
    lockAwareFetch
  )

  if (!isOutageHappened) return children

  return (
    <div className={styles.outageBannerContainer}>
      <div>
        <AppLogo />
        <br />

        <svg
          width='120'
          height='120'
          viewBox='0 0 120 120'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <rect x='20' y='20' width='80' height='80' rx='16' stroke='white' strokeWidth='4' />

          <rect x='10' y='45' width='10' height='30' rx='4' stroke='white' strokeWidth='4' />

          <rect x='100' y='45' width='10' height='30' rx='4' stroke='white' strokeWidth='4' />

          <line x1='60' y1='20' x2='60' y2='8' stroke='white' strokeWidth='4' />
          <circle cx='60' cy='6' r='4' fill='white' />

          <line x1='40' y1='45' x2='50' y2='55' stroke='white' strokeWidth='4' />
          <line x1='50' y1='45' x2='40' y2='55' stroke='white' strokeWidth='4' />
          <circle cx='75' cy='50' r='5' stroke='white' strokeWidth='4' />

          <path
            d='M40 75 Q45 70 50 75 Q55 80 60 75 Q65 70 70 75 Q75 80 80 75'
            stroke='white'
            strokeWidth='4'
            fill='none'
          />
        </svg>

        <h1>Service Unavailable</h1>

        <p>We are currently experiencing an outage.</p>
        <p>Our systems are temporarily down.</p>
        <p>Please try again later.</p>
      </div>
    </div>
  )
}
